import { ExpenseReport } from 'types/embedded/expenseReport';

type ExpenseTableRowType =
  | 'transaction'
  | 'subarticleTotal'
  | 'articleTotal'
  | 'referralTotal'
  | 'grandTotal';

export type ExpenseTableRow = {
  id: string;
  rowType: ExpenseTableRowType;
  date?: string;
  referralName: string;
  expenseId?: string;
  expenseType?: string;
  articleName: string;
  subarticleName: string;
  merchantAccountAmount?: string;
  merchantAccountCurrency?: string;
  merchantAccountCurrencyName?: string;
  selectedCurrencyAmount?: string;
  selectedCurrencyName?: string;
  selectedCurrency?: string;
  comment?: string;
  approver?: string;
  amount?: string;
  currencyCode: string;
  currencyName: string;
  totalAmount?: string;
};

// Идентификатор подстатьи "Конвертация".
// Для её транзакций колонка "Тип расхода" всегда показывает фиксированный
// текст вместо реального типа платежа.
const CONVERSION_SUBARTICLE_ID = 'conversion';

const EXPENSE_TYPE_OVERRIDE_BY_SUBARTICLE_ID: Record<string, string> = {
  [CONVERSION_SUBARTICLE_ID]: 'Потеря на курсе',
};

const getExpenseTypeLabel = (typeName: string, subarticleLabelId: string): string => {
  return EXPENSE_TYPE_OVERRIDE_BY_SUBARTICLE_ID[subarticleLabelId] ?? typeName;
};

const getComment = (comment: string, subarticleId: string): string => {
  if (subarticleId !== CONVERSION_SUBARTICLE_ID) {
    return comment;
  }
  return comment || '–';
};

export const buildExpenseReportTableRows = (data: ExpenseReport): ExpenseTableRow[] => {
  const rows: ExpenseTableRow[] = [];
  let idCounter = 0;

  if (!data.referrals) {
    return rows;
  }

  data.referrals.forEach((referral) => {
    referral.articles.forEach((article) => {
      article.subarticles.forEach((subarticle) => {
        subarticle.transactions.forEach((transaction) => {
          rows.push({
            id: `transaction-${idCounter++}`,
            rowType: 'transaction',
            date: transaction.date,
            referralName: referral.label.name,
            expenseId: transaction.bankOperationId.toString(),
            expenseType: getExpenseTypeLabel(subarticle.type.name, subarticle.label.id),
            articleName: article.label.name,
            subarticleName: subarticle.label.name,
            merchantAccountAmount: transaction.amount.amount,
            merchantAccountCurrency: transaction.amount.currencyCode,
            merchantAccountCurrencyName: transaction.amount.currencyName,
            selectedCurrencyAmount: transaction.convertedAmount.amount,
            selectedCurrency: transaction.convertedAmount.currencyCode,
            selectedCurrencyName: transaction.convertedAmount.currencyName,
            comment: getComment(transaction.comment, subarticle.label.id),
            approver: transaction.user,
            amount: transaction.convertedAmount.amount,
            currencyCode: transaction.convertedAmount.currencyCode,
            currencyName: transaction.convertedAmount.currencyName,
          });
        });

        // Итог по подстатье
        rows.push({
          id: `subarticle-total-${idCounter++}`,
          rowType: 'subarticleTotal',
          referralName: referral.label.name,
          expenseType: '',
          articleName: article.label.name,
          subarticleName: subarticle.label.name,
          totalAmount: subarticle.total.amount,
          currencyCode: subarticle.total.currencyCode,
          currencyName: subarticle.total.currencyName,
        });
      });

      // Итог по статье
      rows.push({
        id: `article-total-${idCounter++}`,
        rowType: 'articleTotal',
        referralName: referral.label.name,
        expenseType: '',
        articleName: article.label.name,
        subarticleName: '',
        totalAmount: article.total.amount,
        currencyCode: article.total.currencyCode,
        currencyName: article.total.currencyName,
      });
    });

    // Итог по рефералу
    rows.push({
      id: `referral-total-${idCounter++}`,
      rowType: 'referralTotal',
      referralName: referral.label.name,
      expenseType: '',
      articleName: '',
      subarticleName: '',
      totalAmount: referral.total.amount,
      currencyCode: referral.total.currencyCode,
      currencyName: referral.total.currencyName,
    });
  });

  // Общий итог
  rows.push({
    id: 'grand-total',
    rowType: 'grandTotal',
    referralName: '',
    expenseType: '',
    articleName: '',
    subarticleName: '',
    totalAmount: data.total.amount,
    currencyCode: data.total.currencyCode,
    currencyName: data.total.currencyName,
  });

  return rows;
};
